import { idb, offlineQueue, type QueueEntry } from './idb';
import type {
  RegisterResponse,
  Circle,
  CircleMember,
  CardTemplate,
  SentCard,
  SendCardRequest,
  CreateCircleRequest,
  CreateCircleResponse,
  JoinCircleRequest,
  AckCardRequest,
  CreateCardTemplateRequest,
  UpdateCardTemplateRequest,
  UpdateUserRequest,
  User,
  PushSubscriptionPayload,
} from '@nudge/shared';

const API_URL = (import.meta.env.VITE_API_URL as string) || '';

export class ApiError extends Error {
  constructor(public status: number, public code: string, message?: string) {
    super(message || code);
  }
}

export class OfflineError extends Error {
  constructor() { super('offline'); }
}

interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  auth?: boolean;
  queueable?: boolean;
}

async function refreshToken(): Promise<string | null> {
  const deviceId = await idb.getDeviceId();
  if (!deviceId) return null;
  try {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_id: deviceId }),
    });
    if (!res.ok) return null;
    const data = await res.json() as { token: string };
    await idb.setToken(data.token);
    return data.token;
  } catch {
    return null;
  }
}

export async function request<T>(path: string, opts: RequestOpts = {}, _retry = false): Promise<T> {
  const url = `${API_URL}${path}`;
  const headers: Record<string, string> = { 'Accept': 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.auth ?? true) {
    const tok = await idb.getToken();
    if (tok) headers['Authorization'] = `Bearer ${tok}`;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (e) {
    if (opts.queueable) {
      const id = (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID() : `q-${Date.now()}-${Math.random()}`;
      await offlineQueue.add({
        id,
        action: path.includes('/cards/send') ? 'send' : path.includes('/cards/clear/') ? 'clear' : 'ack',
        payload: opts.body,
        url: path,
        method: opts.method ?? 'POST',
        timestamp: Date.now(),
      });
      throw new OfflineError();
    }
    throw e;
  }

  if (res.status === 401 && !_retry && (opts.auth ?? true)) {
    const fresh = await refreshToken();
    if (fresh) return request<T>(path, opts, true);
  }

  if (!res.ok) {
    let body: { error?: string; message?: string } = {};
    try { body = await res.json() as typeof body; } catch { /* ignore */ }
    throw new ApiError(res.status, body.error ?? `http_${res.status}`, body.message);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export async function flushQueue(): Promise<number> {
  const entries: QueueEntry[] = await offlineQueue.list();
  let sent = 0;
  for (const e of entries.sort((a, b) => a.timestamp - b.timestamp)) {
    try {
      await request(e.url, { method: e.method as 'POST' | 'PUT' | 'DELETE', body: e.payload, queueable: false });
      await offlineQueue.remove(e.id);
      sent++;
    } catch (err) {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        // Drop bad request — won't succeed on retry
        await offlineQueue.remove(e.id);
      } else {
        // Network still bad — stop and try later
        break;
      }
    }
  }
  return sent;
}

export const api = {
  // auth
  register: (deviceId: string, displayName: string) =>
    request<RegisterResponse>('/auth/register', {
      method: 'POST',
      auth: false,
      body: { device_id: deviceId, display_name: displayName },
    }),
  refresh: (deviceId: string) =>
    request<RegisterResponse>('/auth/refresh', {
      method: 'POST',
      auth: false,
      body: { device_id: deviceId },
    }),
  oauth: (provider: 'google' | 'apple', credential: string, deviceId?: string, displayName?: string) =>
    request<RegisterResponse>('/auth/oauth', {
      method: 'POST',
      auth: true,
      body: { provider, credential, device_id: deviceId, display_name: displayName },
    }),

  // user
  me: () => request<{ user: User }>('/user/me'),
  updateMe: (body: UpdateUserRequest) =>
    request<{ user: User }>('/user/me', { method: 'PUT', body }),
  deleteMe: () => request<void>('/user/me', { method: 'DELETE' }),
  exportMe: async (): Promise<Blob> => {
    const tok = await idb.getToken();
    const res = await fetch(`${API_URL}/user/export`, {
      headers: tok ? { 'Authorization': `Bearer ${tok}` } : undefined,
    });
    if (!res.ok) throw new ApiError(res.status, 'export_failed');
    return await res.blob();
  },

  // circles
  createCircle: (body: CreateCircleRequest) =>
    request<CreateCircleResponse>('/circles', { method: 'POST', body }),
  getCircle: (id: string) =>
    request<{ circle: Circle; members: CircleMember[] }>(`/circles/${id}`),
  lookupInvite: (code: string) =>
    request<{ circle: Circle }>(`/circles/lookup/${code}`),
  joinCircle: (body: JoinCircleRequest) =>
    request<{ circle: Circle }>('/circles/join', { method: 'POST', body }),
  removeMember: (circleId: string, userId: string) =>
    request<void>(`/circles/${circleId}/members/${userId}`, { method: 'DELETE' }),
  regenInvite: (circleId: string) =>
    request<{ invite_code: string; invite_url: string }>(`/circles/${circleId}/invite/regen`, { method: 'POST' }),
  renameCircle: (circleId: string, name: string) =>
    request<{ circle: Circle }>(`/circles/${circleId}`, { method: 'PUT', body: { name } }),
  transferOwnership: (circleId: string, userId: string) =>
    request<void>(`/circles/${circleId}/transfer/${userId}`, { method: 'POST' }),

  // cards
  getTemplates: (circleId: string) =>
    request<{ templates: CardTemplate[] }>(`/cards/templates/${circleId}`),
  sendCard: (body: SendCardRequest) =>
    request<{ sent_card: SentCard }>('/cards/send', { method: 'POST', body, queueable: true }),
  clearCard: (id: string) =>
    request<void>(`/cards/clear/${id}`, { method: 'POST', queueable: true }),
  getActive: (circleId: string) =>
    request<{ cards: SentCard[] }>(`/cards/active/${circleId}`),
  ackCard: (id: string, body: AckCardRequest) =>
    request<void>(`/cards/ack/${id}`, { method: 'POST', body, queueable: true }),
  createTemplate: (body: CreateCardTemplateRequest) =>
    request<{ template: CardTemplate }>('/cards/templates', { method: 'POST', body }),
  updateTemplate: (id: string, body: UpdateCardTemplateRequest) =>
    request<{ template: CardTemplate }>(`/cards/templates/${id}`, { method: 'PUT', body }),
  deleteTemplate: (id: string) =>
    request<void>(`/cards/templates/${id}`, { method: 'DELETE' }),
  reorderTemplates: (updates: Array<{ id: string; sort_order: number }>) =>
    request<void>('/cards/templates/reorder', { method: 'PUT', body: { updates } }),
  favoriteTemplate: (id: string) =>
    request<void>(`/cards/templates/${id}/favorite`, { method: 'POST' }),
  unfavoriteTemplate: (id: string) =>
    request<void>(`/cards/templates/${id}/favorite`, { method: 'DELETE' }),

  // push
  pushSubscribe: (body: PushSubscriptionPayload) =>
    request<{ ok: boolean }>('/push/subscribe', { method: 'POST', body }),
  pushUnsubscribe: (endpoint: string) =>
    request<void>('/push/subscribe', { method: 'DELETE', body: { endpoint } }),
};
